import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  ExternalLink, MapPin, Building2, Users, Globe, Mail, Search,
  Eye, ChevronLeft, ChevronRight, MoreVertical, Trash2
} from 'lucide-react';
import { useAdminClientProfiles } from '@/lib/hooks/use-admin-client-profiles';
import { adminService } from '@/lib/api/services/admin.service';
import { cn } from '@/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const PAGE_SIZE = 10;

// Stat card component
function StatCard({
  icon: Icon,
  count,
  label,
  iconBgClass = 'bg-[rgba(70,105,237,0.10)]'
}: {
  icon: React.ElementType;
  count: number;
  label: string;
  iconBgClass?: string;
}) {
  return (
    <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center">
      <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mb-4", iconBgClass)}>
        <Icon className="w-6 h-6 text-[#4669ed]" />
      </div>
      <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
        {count}
      </div>
      <div className="text-sm text-slate-500 mt-2">
        {label}
      </div>
    </div>
  );
}

// Company size badge component
function CompanySizeBadge({ size }: { size: string | null | undefined }) {
  if (!size) return null;

  const labels: Record<string, string> = {
    startup: 'Startup (1-10)',
    small: 'Small (11-50)',
    medium: 'Medium (51-200)',
    enterprise: 'Enterprise (200+)'
  };

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(70,105,237,0.10)] text-[#4669ed]">
      <Users className="w-3 h-3" />
      {labels[size] || size}
    </span>
  );
}

export default function ClientProfileManagement() {
  const { profiles, isLoading, error, isAdmin } = useAdminClientProfiles();
  const queryClient = useQueryClient();
  const [nameSearch, setNameSearch] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (profileId: string) => adminService.deleteClientProfile(profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-profiles'] });
      toast.success('Client profile deleted successfully');
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error('Failed to delete client profile');
    },
  });

  // Filtered profiles - search by company name or email
  const filteredProfiles = useMemo(() => {
    if (!nameSearch) return profiles;

    const searchLower = nameSearch.toLowerCase();
    return profiles.filter(profile => {
      const nameMatch = profile.company_name?.toLowerCase().includes(searchLower) || false;
      const emailMatch = profile.email?.toLowerCase().includes(searchLower) || false;
      return nameMatch || emailMatch;
    });
  }, [profiles, nameSearch]);

  // Pagination
  const totalCount = filteredProfiles.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginatedProfiles = filteredProfiles.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Stats
  const stats = useMemo(() => {
    return {
      total: profiles.length,
      withWebsite: profiles.filter(p => p.website_url).length,
      withBio: profiles.filter(p => p.bio).length,
      enterprise: profiles.filter(p => p.company_size === 'enterprise').length,
    };
  }, [profiles]);

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-slate-500">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-red-600">Error loading profiles: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stat Cards */}
      <div className="flex gap-6">
        <StatCard icon={Building2} count={stats.total} label="Total Clients" />
        <StatCard icon={Globe} count={stats.withWebsite} label="With Website" />
        <StatCard icon={Mail} count={stats.withBio} label="With Bio" />
        <StatCard icon={Users} count={stats.enterprise} label="Enterprise" />
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="flex gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by company name or email..."
              value={nameSearch}
              onChange={(e) => {
                setNameSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10 rounded-[40px] border-slate-200 h-12"
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
        {/* Table Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            All Clients ({totalCount})
          </h3>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-12 text-center text-slate-500">Loading profiles...</div>
        ) : paginatedProfiles.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {nameSearch
              ? `No client profiles found matching "${nameSearch}".`
              : 'No client profiles found.'}
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="bg-slate-50 border-b border-slate-200">
              <div className="grid grid-cols-[2fr_1.5fr_1fr_1.5fr_1fr] px-6 py-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Company</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Industry</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Size</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Location</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</div>
              </div>
            </div>

            {/* Table Rows */}
            <div>
              {paginatedProfiles.map((profile) => {
                const initials = profile.company_name
                  ?.split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || '?';

                return (
                  <div
                    key={profile.id}
                    className="grid grid-cols-[2fr_1.5fr_1fr_1.5fr_1fr] px-6 py-4 border-b border-slate-100 items-center hover:bg-slate-50/50"
                  >
                    {/* Company */}
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 bg-[#4669ed] text-white">
                        <AvatarImage src={profile.profile_picture_url || undefined} />
                        <AvatarFallback className="bg-[#4669ed] text-white font-bold text-sm">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-semibold text-sm text-slate-900">{profile.company_name}</div>
                        {profile.email && (
                          <div className="text-xs text-slate-500">{profile.email}</div>
                        )}
                      </div>
                    </div>

                    {/* Industry */}
                    <div className="text-sm text-slate-700">
                      {profile.industry || '-'}
                    </div>

                    {/* Size */}
                    <div>
                      <CompanySizeBadge size={profile.company_size} />
                    </div>

                    {/* Location */}
                    <div className="text-sm text-slate-700 flex items-center gap-1">
                      {profile.location ? (
                        <>
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {profile.location}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/client/${profile.id}`}
                        className="w-8 h-8 rounded-md bg-[rgba(70,105,237,0.10)] flex items-center justify-center text-[#4669ed] hover:bg-[rgba(70,105,237,0.20)]"
                      >
                        <Eye className="w-5 h-5" />
                      </Link>
                      {profile.website_url && (
                        <a
                          href={profile.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => setDeleteTarget({ id: profile.id, name: profile.company_name || profile.email || 'Unknown' })}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Client
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-6 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} clients
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {(() => {
                const maxVisible = 5;
                let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
                const endPage = Math.min(totalPages, startPage + maxVisible - 1);
                startPage = Math.max(1, endPage - maxVisible + 1);
                return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                  const pageNum = startPage + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={cn(
                        "w-8 h-8 rounded-md border flex items-center justify-center text-sm font-bold",
                        page === pageNum
                          ? "bg-[#4669ed] border-[#4669ed] text-white"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                });
              })()}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This will permanently remove the client profile and all associated search history. The user account will remain but they will no longer have a client profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
